<template>
  <tr class="quote-row" @click="openQuote">
    <td>#{{ quote.QUOTENUM }}</td>
    <td>{{ customerName }}</td>
    <td class="job-name-cell">{{ quote.JOBNAME }}</td>
    <td>{{ formatDate(quote.CREATEDATE) }}</td>
    <td :class="{ 'high-alert': isValidToAlert }">{{ formatDate(quote.VALIDTO) }}</td>
    <td>{{ salespersonName }}</td>
    <td class="order-total-cell">{{ formattedOrderTotal }}</td>
    <td class="status-cell">
      <QuoteStatusSelector :quote="quote" @statusUpdated="$emit('statusUpdated', $event)" />
    </td>
    <td class="context-container" @click.stop>
      <div class="hover-icon" v-click-outside="closeContextMenu" @click="showContextMenu = !showContextMenu">
        <img id="noHover" src="@/assets/three-dots-menu.svg" />
        <img id="hover" src="@/assets/three-dots-menu-hover.svg" />
        <div class="dropdown hover-style no-images" v-if="showContextMenu">
          <div class="context-menu-item" @click="printQuote">
            <span>Print</span>
          </div>
        </div>
      </div>
    </td>
  </tr>
</template>

<script>
import QuoteStatusSelector from './QuoteStatusSelector.vue';
import { calculateQuoteTotal, formatCurrency } from '@/composables/useQuoteLinePricing';

export default {
  name: 'QuoteRow',
  components: {
    QuoteStatusSelector,
  },
  props: {
    quote: {
      type: Object,
      required: true,
    },
  },
  emits: ['printQuote', 'statusUpdated'],
  data() {
    return {
      showContextMenu: false,
    };
  },
  computed: {
    customerName() {
      if (this.quote.company?.NAME) return this.quote.company.NAME;
      return `${this.quote.contact?.FIRSTNAME || ''} ${this.quote.contact?.LASTNAME || ''}`.trim();
    },
    salespersonName() {
      return this.quote.salesperson?.DISPLAYNAME || 'Unassigned';
    },
    formattedOrderTotal() {
      return formatCurrency(calculateQuoteTotal(this.quote));
    },
    isValidToAlert() {
      if ((this.quote.STATUS || '').toLowerCase() === 'converted') return false;

      const date = this.parseDate(this.quote.VALIDTO);
      if (!date) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);

      const daysUntil = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
      return daysUntil <= 7;
    },
  },
  methods: {
    openQuote() {
      this.$router.push({ name: 'quote', params: { quoteid: this.quote.QUOTEID } });
    },
    closeContextMenu() {
      this.showContextMenu = false;
    },
    printQuote() {
      this.closeContextMenu();
      this.$emit('printQuote', this.quote.QUOTEID);
    },
    parseDate(dateString) {
      if (!dateString) return null;
      const timestamp = parseInt(dateString);
      const date = !isNaN(timestamp) ? new Date(timestamp) : new Date(dateString);
      if (date.toString() === 'Invalid Date') return null;
      return date;
    },
    formatDate(dateString) {
      const date = this.parseDate(dateString);
      if (!date) return '';
      return date.toLocaleDateString('en-US');
    },
  },
};
</script>

<style lang="scss" scoped>
.quote-row {
  cursor: pointer;

  td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.high-alert {
      font-weight: 600;
      color: $alertColor;
    }

    &.status-cell,
    &.context-container {
      overflow: visible;
    }

    &.status-cell {
      position: relative;

      &:has(.is-open) {
        z-index: 20;
      }
    }
  }

  .job-name-cell {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  td.context-container {
    .hover-icon {
      position: relative;
      overflow: visible;

      .dropdown {
        right: 0;
        top: 105%;
      }
    }
  }
}
</style>
